import { FlexView, Form } from '@facile-it/shark-morph/components'
import type { FormGroup } from '@facile-it/shark-morph/components/Form/FormGroup'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import * as React from 'react'
import buildTestSelector from '../../Env/testSelector/buildTestSelector'
import type { FormWorker } from '../FormWorker'
import type { Unit } from '../Unit'
import type { Structure } from '../formBuilder'
import type { ValidatorMapResult, ValidatorObservableSubject } from '../validators/ValidatorResult'
import { ValidationResultType } from '../validators/ValidatorResult'
import { buildItemProps } from './buildItemProp'

export namespace UnitFormRenderer {
    export interface Props<C extends Unit.UnionFields['c']> {
        formName: Form.Props['name']
        onChange: (value: Unit.pickValueType<C>, data: Unit.pickDateType<C>) => void
        field: FormWorker.InternalValueField<C>
        validatorResult: FormWorker.InternalValidatorField
        unit: Unit.FormUnit<C, string>
        view: Structure<string>[0]['rows'][0]['units'][0]['view']
    }
}

export class UnitFormRenderer<C extends Unit.UnionFields['c']> extends React.PureComponent<UnitFormRenderer.Props<C>> {
    private buildFormGroupValidatorProps(): Pick<FormGroup.Props, 'validationResult'> {
        const buildProps = (vrt: 'error' | 'warning' | 'success', vrs: ValidatorObservableSubject[]) => ({
            validationResult: {
                type: vrt,
                messages: vrs.map(v => v.message),
            },
        })

        const r = this.props.validatorResult

        if (r.isValid && this.props.unit.showOnlyValidation === 'error-warning') {
            return {}
        }

        const filterByType = (validatorResults: ValidatorMapResult, vrt: ValidationResultType) =>
            Object.values(validatorResults).filter(v => v.type === vrt)

        switch (r.isValid) {
            case 'unknown':
                return {}
            case 'valid':
            case 'notValid': {
                const errors = filterByType(r.validatorResults, ValidationResultType.ERROR)
                if (errors.length > 0) {
                    return buildProps(ValidationResultType.ERROR, errors)
                }

                const fails = filterByType(r.validatorResults, ValidationResultType.FAIL)
                if (fails.length > 0) {
                    return buildProps(ValidationResultType.ERROR, fails)
                }

                const warnings = filterByType(r.validatorResults, ValidationResultType.WARNING)
                if (warnings.length > 0) {
                    return buildProps(ValidationResultType.WARNING, warnings)
                }

                return buildProps(ValidationResultType.SUCCESS, [])
            }
        }
    }

    private renderLabel() {
        const { unit, field } = this.props

        if (!unit.label) {
            return null
        }

        return (
            <Form.FormGroup.Prefix>
                <label htmlFor={field.name}>{unit.label}</label>
            </Form.FormGroup.Prefix>
        )
    }

    private renderSuffix() {
        if (!this.props.view || !this.props.view.suffix) {
            return null
        }

        return <Form.FormGroup.Suffix>{this.props.view.suffix}</Form.FormGroup.Suffix>
    }

    private retriveDisalbed(field: UnitFormRenderer.Props<Unit.UnionFields['c']>['field']) {
        return pipe(
            O.fromNullable(field.disabled),
            O.match(
                () => ({}),
                d => ({ disabled: d })
            )
        )
    }

    private retriveOptions(field: UnitFormRenderer.Props<Unit.UnionFields['c']>['field']) {
        return pipe(
            O.fromNullable(field.options),
            O.match(
                () => ({}),
                d => ({ options: d })
            )
        )
    }

    static componentTypeToWait: { [k in Unit.UnionFields['c']]?: number } = {
        input: 5000,
        htmlArea: 5000,
        textArea: 5000,
        dateTime: 3000,
    }

    render() {
        return (
            <FlexView.Item {...buildItemProps(this.props.view)}>
                <Form.FormGroup
                    {...this.buildFormGroupValidatorProps()}
                    waitToShowNegativeValidation={pipe(
                        O.fromNullable(this.props.unit.waitToShowNegativeValidation),
                        O.alt(() =>
                            O.fromNullable(UnitFormRenderer.componentTypeToWait[`${this.props.unit.componentType}`])
                        ),
                        O.match(
                            (): FormGroup.Props['waitToShowNegativeValidation'] => ({ type: false }),
                            n => ({ type: true, wait: n })
                        )
                    )}
                    flex={!this.props.view ? false : this.props.view.flex || false}
                    {...buildTestSelector(`${this.props.formName}-${this.props.field.name}`)}
                >
                    {this.renderLabel()}
                    <Form.FormGroup.Content
                        fillAllWidth={
                            this.props.unit.componentType === 'selectExploded' ||
                            this.props.unit.componentType === 'checkList'
                        }
                        fullWidth={this.props.unit.componentType === 'inputTable'}
                    >
                        {React.createElement(
                            this.props.unit.component,
                            // FIX_AFTER_TS_UPDATE
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            {
                                ...this.props.unit.props,
                                ...this.retriveDisalbed(this.props.field),
                                ...this.retriveOptions(this.props.field),
                                name: this.props.field.name,
                                onChange: this.props.onChange,
                                value: this.props.field.value,
                                data: this.props.field.data,
                            }
                        )}
                    </Form.FormGroup.Content>
                    {this.renderSuffix()}
                </Form.FormGroup>
            </FlexView.Item>
        )
    }
}
